import { useEffect, useRef, useState } from "react";
import {
  Button,
  Input,
  Label,
  ListBox,
  Select,
  TextField,
} from "@heroui/react";
import { Icon } from "@iconify/react";

import { OrderStatus } from "@/features/order/types.ts";
import { orderTools } from "@/shared/helper/order.ts";
import { useCities } from "@/features/city/use-cities.ts";

export type OrdersFiltersValue = {
  orderId?: number;
  fromCity?: number;
  toCity?: number;
  status?: string;
  // Даты в формате «YYYY-MM-DD» — как в инпуте и в адресной строке.
  createdFrom?: string;
  createdTo?: string;
  // Телефоны — как ввели; на сервере сравниваются по цифрам, поиск по вхождению.
  fromPhone?: string;
  toPhone?: string;
};

// Ключ «Все» у Select: null в selectedKey означает «ничего не выбрано»,
// а нам нужен видимый пункт, которым фильтр снимается.
const ALL = "all";

// Порядок как в жизненном цикле заказа, а не по алфавиту.
const STATUSES: OrderStatus[] = [
  "created",
  "accepted",
  "going_to_sender",
  "submitted",
  "accounted",
  "checked",
  "revision",
  "dispatched",
  "transit",
  "arrived",
  "assigned",
  "delivers_recipient",
  "completed",
  "cancelled",
  "decided",
];

// Текстовые фильтры не дёргают сервер на каждый символ.
const DEBOUNCE_MS = 400;

/** Из «SP-00239», «00239» или «239» достаём 239. Мусор → undefined. */
function parseOrderId(value: string): number | undefined {
  const digits = value.replace(/\D/g, "");

  if (!digits) return undefined;

  const parsed = Number(digits);

  return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
}

/**
 * Текстовый фильтр с дебаунсом: поле живёт локально, наружу значение уходит
 * через DEBOUNCE_MS. `canonical` приводит введённое к тому виду, в котором оно
 * лежит в адресной строке, — иначе «SP-00239» после применения переписалось бы
 * на «239» прямо под курсором.
 */
function useDebouncedFilter(
  external: string,
  canonical: (raw: string) => string,
  apply: (raw: string) => void,
): [string, (next: string) => void] {
  const [value, setValue] = useState(external);
  const [seen, setSeen] = useState(external);

  // Отложенный вызов должен видеть свежие фильтры, а не те, что были в момент
  // набора: иначе после «Сбросить» сработавший таймер вернул бы остальные
  // фильтры обратно из старого замыкания.
  const applyRef = useRef(apply);

  useEffect(() => {
    applyRef.current = apply;
  });

  // Поле должно догонять адресную строку — например при переходе «назад».
  // Правка стейта прямо в рендере, а не в эффекте: так React перерисовывает
  // сразу, без лишнего кадра.
  if (seen !== external) {
    setSeen(external);

    if (canonical(value) !== external) setValue(external);
  }

  useEffect(() => {
    if (canonical(value) === external) return;

    const timer = setTimeout(() => applyRef.current(value), DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [value]);

  return [value, setValue];
}

const orderIdCanonical = (raw: string) => {
  const parsed = parseOrderId(raw);

  return parsed ? String(parsed) : "";
};

export function OrdersFilters({
  value,
  onChange,
}: {
  value: OrdersFiltersValue;
  onChange: (next: OrdersFiltersValue) => void;
}) {
  const cities = useCities();

  const [orderId, setOrderId] = useDebouncedFilter(
    value.orderId ? String(value.orderId) : "",
    orderIdCanonical,
    (raw) => onChange({ ...value, orderId: parseOrderId(raw) }),
  );

  const [fromPhone, setFromPhone] = useDebouncedFilter(
    value.fromPhone ?? "",
    (raw) => raw,
    (raw) => onChange({ ...value, fromPhone: raw || undefined }),
  );

  const [toPhone, setToPhone] = useDebouncedFilter(
    value.toPhone ?? "",
    (raw) => raw,
    (raw) => onChange({ ...value, toPhone: raw || undefined }),
  );

  const cityItems = [
    { key: ALL, title: "Все города" },
    ...cities.data.data.map((item) => ({
      key: `${item.id}`,
      title: item.name,
    })),
  ];

  const statusItems = [
    { key: ALL, title: "Все статусы" },
    ...STATUSES.map((status) => ({
      key: status as string,
      title: orderTools.status.toString(status),
    })),
  ];

  const isDirty = Boolean(
    value.orderId ||
      value.fromCity ||
      value.toCity ||
      value.status ||
      value.createdFrom ||
      value.createdTo ||
      value.fromPhone ||
      value.toPhone,
  );

  const onReset = () => {
    setOrderId("");
    setFromPhone("");
    setToPhone("");
    onChange({});
  };

  return (
    <div className="flex flex-row flex-wrap gap-3 items-end">
      <TextField className="w-44" value={orderId} onChange={setOrderId}>
        <Label>Номер заказа</Label>
        <Input placeholder="SP-00239" />
      </TextField>

      <TextField className="w-48" value={fromPhone} onChange={setFromPhone}>
        <Label>Телефон отправителя</Label>
        <Input inputMode="tel" placeholder="+7 777 380 66 02" />
      </TextField>

      <TextField className="w-48" value={toPhone} onChange={setToPhone}>
        <Label>Телефон получателя</Label>
        <Input inputMode="tel" placeholder="+7 777 380 66 02" />
      </TextField>

      <Select
        className="w-44"
        selectedKey={value.fromCity ? `${value.fromCity}` : ALL}
        onSelectionChange={(key) =>
          onChange({
            ...value,
            fromCity: !key || key === ALL ? undefined : Number(key),
          })
        }
      >
        <Label>Откуда</Label>
        <Select.Trigger>
          <Select.Value />
          <Select.Indicator />
        </Select.Trigger>
        <Select.Popover>
          <ListBox items={cityItems}>
            {({ key, title }) => (
              <ListBox.Item id={key} textValue={title}>
                {title}
              </ListBox.Item>
            )}
          </ListBox>
        </Select.Popover>
      </Select>

      <Select
        className="w-44"
        selectedKey={value.toCity ? `${value.toCity}` : ALL}
        onSelectionChange={(key) =>
          onChange({
            ...value,
            toCity: !key || key === ALL ? undefined : Number(key),
          })
        }
      >
        <Label>Куда</Label>
        <Select.Trigger>
          <Select.Value />
          <Select.Indicator />
        </Select.Trigger>
        <Select.Popover>
          <ListBox items={cityItems}>
            {({ key, title }) => (
              <ListBox.Item id={key} textValue={title}>
                {title}
              </ListBox.Item>
            )}
          </ListBox>
        </Select.Popover>
      </Select>

      <Select
        className="w-56"
        selectedKey={value.status ?? ALL}
        onSelectionChange={(key) =>
          onChange({
            ...value,
            status: !key || key === ALL ? undefined : String(key),
          })
        }
      >
        <Label>Статус</Label>
        <Select.Trigger>
          <Select.Value />
          <Select.Indicator />
        </Select.Trigger>
        <Select.Popover>
          <ListBox items={statusItems}>
            {({ key, title }) => (
              <ListBox.Item id={key} textValue={title}>
                {title}
              </ListBox.Item>
            )}
          </ListBox>
        </Select.Popover>
      </Select>

      <TextField
        className="w-40"
        value={value.createdFrom ?? ""}
        onChange={(next) =>
          onChange({ ...value, createdFrom: next || undefined })
        }
      >
        <Label>Создан с</Label>
        <Input type="date" />
      </TextField>

      <TextField
        className="w-40"
        value={value.createdTo ?? ""}
        onChange={(next) =>
          onChange({ ...value, createdTo: next || undefined })
        }
      >
        <Label>по</Label>
        <Input type="date" />
      </TextField>

      {isDirty && (
        <Button variant="ghost" onPress={onReset}>
          Сбросить
          <Icon className="size-5" icon="ion:close" />
        </Button>
      )}
    </div>
  );
}
